import { ApolloClient, InMemoryCache, createHttpLink } from '@apollo/client';
import { ApolloLink } from 'apollo-link';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';
import config from '../config';
import authService from '../auth/authService';
import { AppSyncWsLink } from './appsyncWsLink';

const isDevelopment = process.env.NODE_ENV !== 'production';

// Create an HTTP link for queries and mutations
const httpLink = createHttpLink({
  uri: config.appSync.graphqlEndpoint,
});

// Create an auth link that adds the JWT token to the Authorization header
const jwtAuthLink = setContext((_, { headers }) => {
  const token = authService.getToken();

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    }
  };
});

// Subscription transport: in-tree pure-WebSocket AppSync client.
//
// We previously used aws-appsync-subscription-link@3.1.x, but its v3 line
// still negotiates the legacy MQTT transport during the handshake and
// AppSync now rejects MQTT outright ("[GraphQL error]: Subscriptions over
// MQTT is not supported." + HTTP 400, plus a runtime
// `subscriptionFailedCallback` TypeError). v4.x of that package is
// WebSocket-only but requires @apollo/client@4.x as a peer; the rest of
// this app pins @apollo/client@^3.8.0, so a small in-tree shim is the
// safe path. See ./appsyncWsLink.js for the AppSync real-time WS protocol
// implementation; AWS_LAMBDA authorization mode passes the JWT in the
// AppSync header object (no `Bearer` prefix added here — the token
// provider returns the exact value the Lambda authorizer expects).
const subscriptionLink = new AppSyncWsLink({
  graphqlEndpoint: config.appSync.graphqlEndpoint,
  tokenProvider: () => {
    const token = authService.getToken();
    return token ? `Bearer ${token}` : '';
  },
});

// Create an error handling link
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, locations, path }) =>
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      )
    );
  if (networkError) {
    console.error(`[Network error]:`, networkError);
    if (networkError.name === 'ServerError' && networkError.statusCode === 401) {
      console.error('WebSocket authentication error. Check your token format and validity.');
    }
  }
});

// Combine the links
const link = ApolloLink.from([
  // Add error handling link
  errorLink,
  // Always use JWT auth link to add Authorization header if user is logged in
  jwtAuthLink,
  // Route subscriptions to the WS link, queries/mutations to the HTTP link.
  // `getMainDefinition` walks the document for the OperationDefinition node
  // — required because subscriptions that interpolate a fragment (e.g.
  // `ON_CALL_STATE_UPDATE = gql`${CALL_SESSION_FRAGMENT} subscription …`)
  // put a FragmentDefinition at `definitions[0]`, which has no `.operation`
  // property. The previous `definitions[0].operation === 'subscription'`
  // predicate misrouted those subscriptions to httpLink, which AppSync
  // rejected with 400 "Subscriptions over MQTT is not supported."
  ApolloLink.split(
    ({ query }) => {
      const def = getMainDefinition(query);
      return def.kind === 'OperationDefinition' && def.operation === 'subscription';
    },
    subscriptionLink,
    httpLink
  ),
]);

// Create the Apollo Client with cache configuration
const client = new ApolloClient({
  link,
  devtools: {
    enabled: isDevelopment,
  },
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
  },
  // typePolicies tell Apollo's InMemoryCache how to normalize voice-call
  // entities (CallSession/CallEvent/TranscriptSegment/RecordingAsset) and
  // how to handle the listRecentCallSessions polling field. Without this,
  // every poll fires Apollo invariant #15 ("Cache data may be lost when
  // replacing the listRecentCallSessions field of a Query object") because
  // the rows lack the default Apollo identity field (`id`/`_id`) and the
  // list shape has no `keyArgs`, so each poll attempts a structural merge
  // and the cache writer logs a warning.
  //
  // CallSession is keyed by callId; the per-call query/subscription helpers
  // use the same identity. CallEvent is keyed by (callId, eventId).
  // TranscriptSegment by (callId, segmentId). RecordingAsset by
  // (callId, recordingId). listRecentCallSessions is treated as
  // "incoming wins" — each poll is the authoritative active set, scoped
  // separately by activeOnly so the active and full views don't trample
  // each other.
  cache: new InMemoryCache({
    typePolicies: {
      CallSession: { keyFields: ['callId'] },
      CallEvent: { keyFields: ['callId', 'eventId'] },
      TranscriptSegment: { keyFields: ['callId', 'segmentId'] },
      RecordingAsset: { keyFields: ['callId', 'recordingId'] },
      Query: {
        fields: {
          listRecentCallSessions: {
            keyArgs: ['activeOnly'],
            merge(_existing, incoming) { return incoming; },
          },
          listCallEvents: {
            keyArgs: ['callId'],
            merge(_existing, incoming) { return incoming; },
          },
          listCallTranscripts: {
            keyArgs: ['callId'],
            merge(_existing, incoming) { return incoming; },
          },
          listCallRecordings: {
            keyArgs: ['callId'],
            merge(_existing, incoming) { return incoming; },
          },
        },
      },
    },
  }),
});

export default client;
